import React, { useCallback, Fragment, ReactElement } from 'react';
import { useNavigate } from 'react-router';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Widget from '../../../components/widget/widget.component';
import Image from '../../../components/image/image.component';
import LetterAvatar from '../../../components/letter-avatar/letter-avatar.component';
import FilteredText from '../../../components/filtered-text/filtered-text.component';
import RichTextBlock from '../../../components/rich-text-block/rich-text-block.component';
import FormatService from '../../../services/format-service';
import { HTML_TAG_NAME_ANCHOR } from '../../../constants/system/html-tag-names';
import { CONTACT_VIEW, SEARCH_RESULT_VIEW } from '../../../constants/entity/permissions';
import { WIDGET_NAME_USER_MENTIONS } from '../../../constants/system/widget-names';

const DashboardWidgetUserMentions: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'My Mentions';

  const {
    currentUser,
    commentListData,
    widgetSettings,
    onWidgetSettingsChange,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();

  const onRowClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    commentEntityLink: string,
  ): void => {
    const isTargetLink: boolean = event.target
      && (event.target as HTMLElement).tagName.toLowerCase() === HTML_TAG_NAME_ANCHOR;

    if (commentEntityLink && FormatService.isValidLink(commentEntityLink) && !isTargetLink) {
      navigate(commentEntityLink);
    }

    if (isTargetLink) {
      event.stopPropagation();
    }
  }, [currentUser]);

  return (
    <Widget
      isScrollable
      isEmpty={!commentListData || !commentListData.length}
      widgetName={WIDGET_NAME_USER_MENTIONS}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
      widgetSettings={widgetSettings}
      onWidgetSettingsChange={onWidgetSettingsChange}
    >
      <Table>
        <TableBody isPlain withZebraRows withHeaderColumn>
          {
            commentListData.map((commentItem: Comment): ReactElement => (
              <TableRow
                isButton
                key={commentItem.id}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onRowClick(event, commentItem.entityLink);
                }}
              >
                <TableCell width="5%">
                  {
                    !!commentItem.user && (
                      <Fragment>
                        {
                          (!!commentItem.user.photoThumbnail || !!commentItem.user.photo) && (
                            <PaddedFlexContainer>
                              <Image
                                text={commentItem.user.fullName}
                                link={commentItem.user.photoThumbnail || commentItem.user.photo}
                              />
                            </PaddedFlexContainer>
                          )
                        }
                        {
                          (!commentItem.user.photoThumbnail && !commentItem.user.photo) && (
                            <LetterAvatar wordList={[commentItem.user.firstName, commentItem.user.lastName]} />
                          )
                        }
                      </Fragment>
                    )
                  }
                </TableCell>
                <TableCell width="45%">
                  {commentItem.user ? `${commentItem.user.fullName}` : 'Mentioned'}
                  {' '}
                  in
                  {' '}
                  {commentItem.entityTypeText ? `${commentItem.entityTypeText} ` : ''}
                  {commentItem.entityName}
                </TableCell>
                <TableCell isText>
                  <RichTextBlock>
                    <FilteredText
                      withMentions
                      withHashtags
                      text={commentItem.text}
                      mentionPermission={CONTACT_VIEW}
                      hashtagPermission={SEARCH_RESULT_VIEW}
                    />
                  </RichTextBlock>
                </TableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
    </Widget>
  );
});

export default DashboardWidgetUserMentions;
